import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription, throwError } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  tap,
} from 'rxjs/operators';
import { AccountV2Service } from 'src/app/services/account-v2.service';
import { CepResponse, CepService } from 'src/app/services/cep.service';
import { AccountAsyncValidationService } from 'src/app/shared/validators/account-async-validator.service';
import { CustomValidators } from 'src/app/shared/validators/custom-validators';
import { FormValidationComponent } from 'src/app/shared/validators/form-validation-component';
import { cnpjValidator } from 'src/app/_shared/validators/cnpj-validator';
import { cpfValidator } from 'src/app/util/cpf-validator';
import {
  CNPJ_MASK,
  CNPJ_SPECIAL_CHARACTERS,
  normalizeDocumentForSubmit,
} from 'src/app/_shared/utils/document-mask.util';

@Component({
  selector: 'app-contractor',
  templateUrl: './contractor.component.html',
  styleUrls: ['./contractor.component.scss'],
})
export class ContractorComponent
  extends FormValidationComponent
  implements OnInit, OnDestroy
{
  form: FormGroup;
  formLabels: Record<string, string> = {
    cnpj: 'CNPJ',
    cpf: 'CPF',
    name: 'Nome',
    mobile: 'Celular',
    email: 'E-mail',
    zipCode: 'CEP',
    address: 'Endereço',
    addressNumber: 'Número',
    neighborhood: 'Bairro',
    city: 'Cidade',
    complement: 'Complemento',
    state: 'Estado',
  };

  isLoadingForm = false;
  loadingFormMessage = '';
  isLoadingZipCode = false;

  cnpjMask = CNPJ_MASK;
  cnpjSpecialCharacters = CNPJ_SPECIAL_CHARACTERS;

  private subs = new Subscription();
  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly accountV2Service: AccountV2Service,
    private readonly cepService: CepService,
    private readonly accountAsyncValidationService: AccountAsyncValidationService,
    private readonly snack: MatSnackBar
  ) {
    super();
  }

  ngOnInit(): void {
    this.initForm();
    this.loadAccount();
    this.cpfValueChange();
    this.cnpjValueChange();
  }

  get isSaveDisabled(): boolean {
    return this.form.invalid || this.form.pending || this.isLoadingZipCode;
  }

  onSave() {
    if (this.isSaveDisabled) {
      return;
    }

    const formValues = this.form.getRawValue();
    const cpf = formValues.cpf || null;
    const cnpj = formValues.cnpj
      ? normalizeDocumentForSubmit(formValues.cnpj)
      : null;

    if (!cpf && !cnpj) {
      this.snack.open(
        'Informe ao menos um CPF ou CNPJ válido para salvar.',
        'OK',
        { duration: 4000 }
      );
      return;
    }

    this.initLoading('Atualizando dados da conta');

    this.accountV2Service
      .update(
        {
          cnpj,
          cpf,
          name: formValues.name,
          email: formValues.email,
          mobile: formValues.mobile,
        },
        {
          zipCode: formValues.zipCode,
          address: formValues.address,
          addressNumber: formValues.addressNumber,
          neighborhood: formValues.neighborhood,
          complement: formValues.complement,
          city: formValues.city,
          state: formValues.state,
        }
      )
      .pipe(
        finalize(() => this.finalizeLoading())
      )
      .subscribe({
        next: () => {
          this.snack.open(`Conta atualizada com sucesso.`, 'OK', {
            duration: 4000,
          });
        },
        error: (err) => {
          const message = err?.error?.message || err?.message || 'Erro ao atualizar conta. Tente novamente mais tarde.';
          this.snack.open(message, 'OK', {
            duration: 4000,
          });
        }
      });
  }

  private loadAccount() {
    this.subs.add(
      this.accountV2Service.find().subscribe(({ address, ...account }) => {
        this.form.patchValue({
          ...account,
          ...address,
        });

        this.finalizeLoading();
        this.markAsyncValidatedControlsAsTouched();
      })
    );
  }

  private markAsyncValidatedControlsAsTouched() {
    ['cnpj', 'cpf', 'email'].forEach((controlName) => {
      this.form.get(controlName)?.markAsTouched();
    });
  }

  private initForm() {
    this.initLoading('Buscando dados da conta');
    const defaultValue = {
      value: null,
      disabled: true,
    };

    this.form = this.formBuilder.group(
      {
        cnpj: [
          defaultValue,
          [Validators.minLength(14), cnpjValidator()],
          [this.accountAsyncValidationService.accountCnpjAlredyExists()],
        ],
        cpf: [
          defaultValue,
          [Validators.minLength(11), cpfValidator()],
          [this.accountAsyncValidationService.accountCpfAlredyExists()],
        ],
        name: [defaultValue, [Validators.required, Validators.minLength(5)]],
        mobile: [defaultValue, [Validators.required, Validators.minLength(10)]],
        email: [
          defaultValue,
          [Validators.required, Validators.email],
          [this.accountAsyncValidationService.accountEmailAlredyExists()],
        ],
        zipCode: [defaultValue, [Validators.required]],
        address: [defaultValue, [Validators.required]],
        addressNumber: [defaultValue, [Validators.required]],
        neighborhood: [defaultValue, [Validators.required]],
        city: [defaultValue, [Validators.required]],
        complement: [defaultValue, []],
        state: [defaultValue, [Validators.required]],
      },
      {
        validators: [CustomValidators.atLeastOneFilled(['cpf', 'cnpj'])],
      }
    );
  }

  private initLoading(loadingFormMessage: string) {
    this.loadingFormMessage = loadingFormMessage;
    this.isLoadingForm = true;
  }

  private finalizeLoading() {
    this.form.get('cnpj').enable();
    this.form.get('cpf').enable();
    this.form.get('name').enable();
    this.form.get('mobile').enable();
    this.form.get('email').enable();
    this.form.get('zipCode').enable({ emitEvent: false });
    this.form.get('address').enable();
    this.form.get('addressNumber').enable();
    this.form.get('neighborhood').enable();
    this.form.get('complement').enable();
    this.isLoadingForm = false;

    this.zipCodeValueChange();
  }

  private zipCodeValueChange() {
    this.subs.add(
      this.form
        .get('zipCode')
        .valueChanges.pipe(
          debounceTime(500),
          distinctUntilChanged(),
          filter((zipCode) => zipCode?.length === 8),
          tap(() => console.log('passou'))
        )
        .subscribe((zipCode) => {
          this.searchZipCode(zipCode);
        })
    );
  }

  private cpfValueChange() {
    this.subs.add(
      this.form.get('cpf')?.valueChanges.subscribe(() => {
        this.updateCpfCnpjValidation();
      })
    );
  }

  private cnpjValueChange() {
    this.subs.add(
      this.form.get('cnpj')?.valueChanges.subscribe(() => {
        this.updateCpfCnpjValidation();
      })
    );
  }

  private updateCpfCnpjValidation() {
    const cpfControl = this.form.get('cpf');
    const cnpjControl = this.form.get('cnpj');

    const cpf = cpfControl?.value;
    const cnpj = cnpjControl?.value;

    const defaultValidatorsCpf = [Validators.minLength(11), cpfValidator()];
    const defaultValidatorsCnpj = [Validators.minLength(14), cnpjValidator()];

    const conditionalRequired = cpf || cnpj ? [] : [Validators.required];

    cpfControl.setValidators([...defaultValidatorsCpf, ...conditionalRequired]);
    cnpjControl.setValidators([
      ...defaultValidatorsCnpj,
      ...conditionalRequired,
    ]);

    // MANTÉM VALIDADORES ASSÍNCRONOS
    const asyncCnpj =
      this.accountAsyncValidationService.accountCnpjAlredyExists();
    cnpjControl.setAsyncValidators([asyncCnpj]);

    const asyncCpf =
      this.accountAsyncValidationService.accountCpfAlredyExists();
    cpfControl.setAsyncValidators([asyncCpf]);

    cpfControl.updateValueAndValidity({ emitEvent: false });
    cnpjControl.updateValueAndValidity({ emitEvent: false });
  }

  private searchZipCode(zipCode: string) {
    this.initLoadingZipCode();
    this.cepService
      .getAddressByZipCode(zipCode)
      .pipe(
        catchError((error) => {
          this.resetLoadingZipCode();
          this.snack.open(
            'Não foi possível localizar o CEP informado. Preencha o endereço manualmente.',
            'OK',
            { duration: 4000 }
          );
          return throwError(() => error);
        })
      )
      .subscribe((address) => {
        this.finalizeLoadingZipCode(address);
      });
  }

  private initLoadingZipCode() {
    this.form.get('zipCode').disable();
    this.form.get('address').disable();
    this.form.get('complement').disable();
    this.form.get('addressNumber').disable();
    this.form.get('neighborhood').disable();

    this.isLoadingZipCode = true;
  }

  private finalizeLoadingZipCode(address: CepResponse) {
    this.form.patchValue({
      address: address.logradouro,
      neighborhood: address.bairro,
      city: address.localidade,
      state: address.uf,
    });

    this.resetLoadingZipCode();
  }

  private resetLoadingZipCode() {
    this.form.get('zipCode').enable();
    this.form.get('address').enable();
    this.form.get('complement').enable();
    this.form.get('addressNumber').enable();
    this.form.get('neighborhood').enable();

    this.isLoadingZipCode = false;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
